import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from "rxjs";
import {ContractProjectTaskYTD, Project, ProjectEstimate, ProjectTaskAvailableValues, TaskInfo} from '@app/shared/classes/project';
import {CommonTransformationService, QueryResult, YTDEntityType} from '@app/shared';
import {map} from "rxjs/operators";
import {TreeNode} from "primeng/api";
import {ProjectCatalog} from "@app/project/project/project-catalog/project-catalog";


@Injectable()
export class ProjectDataService {
  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  getProjectById(projectId: number): Observable<Project> {
    const url = `/projects/projects/${projectId}`;
    return this.http.get<Project>(url);
  }

  getProjectEstimateById(projectEstimateId: number): Observable<ProjectEstimate> {
    const url = `/projects/estimates/${projectEstimateId}`;
    return this.http.get<ProjectEstimate>(url);
  }

  getTasksByProjectId(projectId: number): Observable<QueryResult> {
    const url = `/projects/tasks/search?project.id=${projectId}`;
    return this.http.get<QueryResult>(url);
  }

  getProjectCatalog(): Observable<QueryResult> {
    const url = '/project/project-catalogs/search';
    return this.http.get<QueryResult>(url);
  }

  getProjectCatalogAsTree(): Observable<TreeNode<ProjectCatalog>[]> {
    return this.getProjectCatalog().pipe(map(res => {
      return <TreeNode<ProjectCatalog>[]>this.transform.convertToTree("name", res.list);
    }));
  }

  saveProject(project: Project): Observable<Project> {
    const url = '/projects/projects';
    return this.http.post<Project>(url, project);
  }

  submitProject(project: Project): Observable<Project> {
    const url = '/approvals/projects';
    return this.http.post<Project>(url, project);
  }

  updateProject(project: Project): Observable<Project> {
    const url = `/projects/projects/${project.id}`;
    return this.http.put<Project>(url, project);
  }

  saveProjectEstimate(projectEstimate: ProjectEstimate): Observable<ProjectEstimate> {
    const url = '/projects/estimates';
    return this.http.post<ProjectEstimate>(url, projectEstimate);
  }


  submitProjectEstimate(projectEstimate: ProjectEstimate): Observable<ProjectEstimate> {
    const url = '/approvals/project-estimates';
    return this.http.post<ProjectEstimate>(url, projectEstimate);
  }

  updateProjectEstimate(projectEstimate: ProjectEstimate): Observable<ProjectEstimate> {
    const url = '/projects/estimates/' + String(projectEstimate.id);
    return this.http.put<ProjectEstimate>(url, projectEstimate);
  }

  getRemainingBudgets(projectId: number): Observable<ProjectTaskAvailableValues> {
    const url = `/projects/tasks/available-values?project.id=${projectId}`;
    return this.http.get<ProjectTaskAvailableValues>(url);
  }

  getYtdValues(ytdEntityType: YTDEntityType, entityId: number): Observable<ContractProjectTaskYTD[]> {
    const url = `/projects/contract-project-task-ytd?ytdEntityType=${ytdEntityType}&entityId=${entityId}`;
    return this.http.get<ContractProjectTaskYTD[]>(url);
  }

  getProjectTaskBalances(projectTaskId: number): Observable<QueryResult> {
    const url = `/projects/project-task-balance?projectTaskId=${projectTaskId}`;
    return this.http.get<QueryResult>(url);
  }

  getRevenueAccountMapping(accountGroupId: number, contractId: number, projectId: number) {
    let url = `/finance/account-mappings/revenue-account-mapping?accountGroup.id=${accountGroupId}`;

    if (contractId) {
      url += `&contract.id=${contractId}`;
    }
    if (projectId) {
      url += `&project.id=${projectId}`;
    }
    return this.http.get<any>(url);
  }

  getTaskInfoByProjectId(projectId: number): Observable<TaskInfo[]> {
    const url = `/projects/task-info/${projectId}`;
    return this.http.get<TaskInfo[]>(url);
  }
}
